import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, Sparkles, Loader2, MessageSquare, Users, Link2, LayoutTemplate, BarChart3, HelpCircle, Lightbulb, Info, Flag } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

const CONTEXT_CONFIG = {
  '/': {
    title: 'Dashboard Assistant',
    description: 'Get insights from your data',
    icon: BarChart3,
    quickActions: [
      { label: 'Summarize recent activity', prompt: 'Summarize the recent activity and key metrics from my dashboard' },
      { label: 'Top insights', prompt: 'What are the most important insights from my data?' },
      { label: 'Suggest next steps', prompt: 'Based on my dashboard data, what actions should I prioritize?' },
    ]
  },
  '/contacts': {
    title: 'Contact Assistant',
    description: 'Search and manage contacts',
    icon: Users,
    quickActions: [
      { label: 'Find duplicates', prompt: 'Help me identify potential duplicate contacts in my database' },
      { label: 'Suggest segments', prompt: 'Based on my contact data, what segments should I create?' },
      { label: 'Data quality check', prompt: 'Analyze my contact data quality and suggest improvements' },
    ]
  },
  '/integrations': {
    title: 'Integration Helper',
    description: 'Get help with data sync',
    icon: Link2,
    quickActions: [
      { label: 'Troubleshoot sync', prompt: 'Help me troubleshoot issues with my integration sync' },
      { label: 'Mapping suggestions', prompt: 'Suggest optimal field mappings for my integration' },
      { label: 'Best practices', prompt: 'What are best practices for keeping my integrations in sync?' },
    ]
  },
  '/landing-pages': {
    title: 'Content Assistant',
    description: 'Create compelling pages',
    icon: LayoutTemplate,
    quickActions: [
      { label: 'Improve headlines', prompt: 'Suggest improvements for my landing page headlines' },
      { label: 'Generate copy', prompt: 'Help me write compelling copy for my landing page' },
      { label: 'CTA optimization', prompt: 'How can I improve my call-to-action buttons?' },
    ]
  },
  '/import': {
    title: 'Import Assistant',
    description: 'Help with data import',
    icon: Users,
    quickActions: [
      { label: 'Clean my data', prompt: 'How should I clean and prepare my data before import?' },
      { label: 'Map fields', prompt: 'Help me map my import columns to the right contact fields' },
      { label: 'Handle duplicates', prompt: 'What should I do about duplicate records during import?' },
    ]
  },
};

const DEFAULT_CONTEXT = {
  title: 'AI Assistant',
  description: 'How can I help you?',
  icon: Sparkles,
  quickActions: [
    { label: 'Ask a question', prompt: '' },
    { label: 'Get help', prompt: 'What can you help me with in this application?' },
  ]
};

export default function AIPanel({ isOpen, onClose }) {
  const { tenantFetch } = useTenant();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const pathname = location.pathname;
  const context = CONTEXT_CONFIG[pathname] || DEFAULT_CONTEXT;
  const ContextIcon = context.icon;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm your ${context.title}. ${context.description}. Try one of the quick actions below or ask me anything!`
      }]);
    }
  }, [isOpen, pathname]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await tenantFetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          context: {
            page: pathname,
            contextType: context.title
          },
          history: messages.slice(-6)
        })
      });

      const data = await res.json();

      if (data.response) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          aiDisclosure: data.ai_disclosure || { is_ai: true, model: 'AI', human_review_available: true },
          userMessage: text,
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I'm sorry, I couldn't process that request. Please try again.",
          aiDisclosure: { is_ai: true, model: 'AI', human_review_available: false },
        }]);
      }
    } catch (err) {
      console.error('AI chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, there was an error connecting to the AI service. Please try again.",
        aiDisclosure: { is_ai: true, model: 'AI', human_review_available: false },
      }]);
    }

    setLoading(false);
  };

  const requestHumanReview = async (msg) => {
    const note = window.prompt(
      'Briefly tell us why you want a human to review this AI answer (optional):',
      ''
    );
    if (note === null) return;  // user cancelled
    try {
      await tenantFetch('/api/ai/human-review-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision_type: 'rag_chat',
          target_type: 'ai_chat',
          page: pathname,
          user_message: msg.userMessage || '',
          ai_response: msg.content || '',
          user_note: note || '',
        }),
      });
      msg._reviewRequested = true;
      setMessages(prev => prev.map(m => m === msg ? { ...m, _reviewRequested: true } : m));
    } catch (err) {
      console.error('human review request failed:', err);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (action) => {
    if (action.prompt) {
      sendMessage(action.prompt);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-teal-500 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <ContextIcon size={20} />
            </div>
            <div>
              <h2 className="font-semibold">{context.title}</h2>
              <p className="text-xs text-white/80">{context.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
              {msg.role === 'assistant' && msg.aiDisclosure?.is_ai && (
                <div className="mt-1 max-w-[85%] flex items-center gap-2 text-[10px] text-gray-500">
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700"
                    title="EU AI Act Article 50: this response was generated by AI."
                  >
                    <Info size={10} />
                    AI-generated{msg.aiDisclosure.model ? ` · ${msg.aiDisclosure.model}` : ''}
                  </span>
                  {msg.aiDisclosure.human_review_available && msg.userMessage && (
                    msg._reviewRequested ? (
                      <span className="text-emerald-600">Human review requested ✓</span>
                    ) : (
                      <button
                        onClick={() => requestHumanReview(msg)}
                        className="inline-flex items-center gap-1 hover:text-purple-700 hover:underline"
                        title="Flag this AI answer for a human to review."
                      >
                        <Flag size={10} />
                        Request human review
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                <Loader2 size={18} className="animate-spin text-purple-600" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-4 pb-2">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
              <Lightbulb size={12} />
              Quick actions
            </p>
            <div className="flex flex-wrap gap-2">
              {context.quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action)}
                  className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-4 border-t bg-gray-50">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 px-4 py-2.5 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="p-2.5 bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={18} />
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export function AIFloatingButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-6 right-6 p-4 bg-gradient-to-r from-purple-600 to-teal-500 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all z-30 group"
      title="Open AI Assistant"
    >
      <Sparkles size={24} className="group-hover:rotate-12 transition-transform" />
    </button>
  );
}
